/**
 * AddChannelModal — Modal for adding new channel integrations.
 */
import { useState, useCallback } from "react";
import { X, Hash, Mail, Phone, Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useChannelStore } from "@/stores/useChannelStore";
import type { ChannelType } from "@ajnt/shared";

const channelTypes: Array<{
  type: ChannelType;
  label: string;
  icon: React.ReactNode;
  fields: Array<{ key: string; label: string; placeholder: string; type?: string }>;
}> = [
  {
    type: "slack",
    label: "Slack",
    icon: <Hash className="h-5 w-5" />,
    fields: [{ key: "webhookUrl", label: "Webhook URL", placeholder: "https://hooks.slack.com/..." }],
  },
  {
    type: "email",
    label: "Email",
    icon: <Mail className="h-5 w-5" />,
    fields: [
      { key: "apiKey", label: "SendGrid API Key", placeholder: "SG.xxxxx", type: "password" },
      { key: "fromEmail", label: "From Email", placeholder: "noreply@example.com" },
    ],
  },
  {
    type: "sms",
    label: "SMS",
    icon: <Phone className="h-5 w-5" />,
    fields: [
      { key: "accountSid", label: "Twilio Account SID", placeholder: "AC..." },
      { key: "authToken", label: "Auth Token", placeholder: "Token", type: "password" },
      { key: "phoneNumber", label: "Phone Number", placeholder: "+1234567890" },
    ],
  },
  {
    type: "telegram",
    label: "Telegram",
    icon: <Send className="h-5 w-5" />,
    fields: [{ key: "botToken", label: "Bot Token", placeholder: "123456:ABC-DEF...", type: "password" }],
  },
  {
    type: "whatsapp",
    label: "WhatsApp",
    icon: <MessageSquare className="h-5 w-5" />,
    fields: [
      { key: "accessToken", label: "Access Token", placeholder: "EAAx...", type: "password" },
      { key: "phoneNumberId", label: "Phone Number ID", placeholder: "123456789" },
    ],
  },
];

interface AddChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddChannelModal({ isOpen, onClose }: AddChannelModalProps) {
  const { createConfig, isLoading } = useChannelStore();
  const [selectedType, setSelectedType] = useState<ChannelType | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const channelDef = channelTypes.find((c) => c.type === selectedType);

  const handleSubmit = useCallback(async () => {
    if (!selectedType) return;
    setError(null);

    // Validate all fields are filled
    const def = channelTypes.find((c) => c.type === selectedType);
    if (def) {
      for (const field of def.fields) {
        if (!credentials[field.key]?.trim()) {
          setError(`${field.label} is required`);
          return;
        }
      }
    }

    const result = await createConfig({
      organizationId: "",
      userId: "",
      channelType: selectedType,
      active: true,
      credentials,
      priority: 1,
    });

    if (result) {
      setSelectedType(null);
      setCredentials({});
      onClose();
    }
  }, [selectedType, credentials, createConfig, onClose]);

  const handleClose = useCallback(() => {
    setSelectedType(null);
    setCredentials({});
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="mx-4 w-full max-w-lg rounded-xl border border-white/10 bg-surface-raised"
        data-testid="add-channel-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">
            {selectedType ? `Configure ${channelDef?.label}` : "Add Channel"}
          </h2>
          <button onClick={handleClose} className="rounded p-1 text-muted hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {!selectedType ? (
            /* Channel type selector */
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="channel-type-selector">
              {channelTypes.map((ct) => (
                <button
                  key={ct.type}
                  type="button"
                  onClick={() => setSelectedType(ct.type)}
                  className="flex flex-col items-center gap-2 rounded-xl border border-white/10 p-4 transition-colors hover:border-primary/50 hover:bg-primary/5"
                  data-testid={`channel-option-${ct.type}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 text-muted">
                    {ct.icon}
                  </div>
                  <span className="text-sm font-medium text-foreground">{ct.label}</span>
                </button>
              ))}
            </div>
          ) : (
            /* Credential form */
            <div className="space-y-4" data-testid="credential-form">
              {channelDef?.fields.map((field) => (
                <Input
                  key={field.key}
                  label={field.label}
                  placeholder={field.placeholder}
                  type={field.type ?? "text"}
                  value={credentials[field.key] ?? ""}
                  onChange={(e) =>
                    setCredentials((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                />
              ))}
              {error && <p className="text-sm text-danger">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-white/10 px-6 py-4">
          {selectedType && (
            <Button variant="ghost" onClick={() => setSelectedType(null)}>
              Back
            </Button>
          )}
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          {selectedType && (
            <Button onClick={handleSubmit} loading={isLoading}>
              Add Channel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
